import { notify } from '@/components/Notice/Notice';

/*
  Handing a stored file to the person as a download. The asset store is another origin in
  production, where a link's `download` attribute is ignored, so the bytes are fetched and handed
  over as a file; a store that refuses the fetch still gets the file opened in a tab. First written
  for an outfit's cover (ALS-235), shared since a media book's photos are downloaded too (ALS-79).

  The asset CDN answers a cross-origin read with CORS headers (ALS-293), but only when the request
  asks for one. A preview's own `img` may already have cached the file without them, and reusing
  that copy would fail the fetch, so this one skips the cache.
*/

export interface DownloadableFile {
    url: string;
    fileName: string;
}

/**
 * Opens a file in a new tab, saying so with `failedMessage` when the browser blocks it. Call it
 * straight from the click, before any `await`, or the browser treats it as a pop-up. It is how a
 * film is handed over: reading up to 500 MB into memory first could take the tab down with it.
 */
export function openFile(url: string, failedMessage: string): void {
    // Not 'noopener' in the features: with it, window.open always answers null, opened or not.
    const opened = window.open(url, '_blank');
    if (opened) opened.opener = null;
    else notify.error(failedMessage);
}

/**
 * A stored file's bytes, read past the browser's cache; throws when the store refuses the fetch.
 * What Download, Print and the document viewer (ALS-404) all start from.
 */
export async function fetchFile(url: string, signal?: AbortSignal): Promise<Blob> {
    const response = await fetch(url, { cache: 'no-store', signal });
    if (!response.ok) throw new Error(String(response.status));
    return response.blob();
}

/**
 * Fetches the bytes and saves them as `fileName`; throws when the store refuses the fetch. For a
 * caller that must know whether the file arrived, such as a report export (ALS-86).
 */
export async function saveFile(url: string, fileName: string): Promise<void> {
    saveBlob(await fetchFile(url), fileName);
}

/** How long a saved file's address is kept after the click, so a phone's browser has taken it first. */
export const SAVE_RELEASE_MS = 60_000;

/**
 * Saves bytes already in the page as `fileName`, with no fetch: how the document viewer hands over
 * the PDF it is showing, whose signed link may have expired since it opened (ALS-493), and how a
 * brand report the page has just fetched is saved (ALS-93). The address is let go of a while after
 * the click rather than at once: the browser starts the download after the click returns, and
 * mobile Safari loses one whose address has already gone.
 */
export function saveBlob(bytes: Blob, fileName: string): void {
    const blob = URL.createObjectURL(bytes);
    const link = document.createElement('a');
    link.href = blob;
    link.download = fileName;
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(blob), SAVE_RELEASE_MS);
}

/** Saves one file; when the fetch fails, opens it in a tab, and says so with `failedMessage` if even that is blocked. */
export async function downloadFile(
    url: string,
    fileName: string,
    failedMessage: string,
): Promise<void> {
    try {
        await saveFile(url, fileName);
    } catch {
        openFile(url, failedMessage);
    }
}

/** The hidden frame the last printed PDF sits in, kept until the next print so its dialog can finish. */
let printFrame: { frame: HTMLIFrameElement; blob: string } | null = null;

/**
 * Prints a PDF straight away: the browser's print dialog for the file, with no tab in between.
 * The bytes are fetched and loaded into a hidden frame of this page's own origin, because a frame
 * holding the asset CDN's URL is another origin, whose print this page may not call. When the
 * fetch fails it says so with `failedMessage`; past the `await` a fallback tab would be blocked.
 */
export async function printFile(url: string, failedMessage: string): Promise<void> {
    let bytes: Blob;
    try {
        bytes = await fetchFile(url);
    } catch {
        notify.error(failedMessage);
        return;
    }
    printBlob(bytes, failedMessage);
}

/**
 * Prints a PDF already in the page, as `printFile` does once it has the bytes: the document viewer
 * prints the copy it is showing rather than its link, which may have expired since (ALS-493).
 */
export function printBlob(bytes: Blob, failedMessage: string): void {
    const blob = URL.createObjectURL(bytes);
    if (printFrame) {
        printFrame.frame.remove();
        URL.revokeObjectURL(printFrame.blob);
    }
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.style.cssText =
        'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden';
    frame.addEventListener('load', () => {
        try {
            frame.contentWindow?.focus();
            frame.contentWindow?.print();
        } catch {
            notify.error(failedMessage);
        }
    });
    frame.src = blob;
    document.body.appendChild(frame);
    printFrame = { frame, blob };
}

/**
 * Saves each file in turn, one after the other, so the browser asks about them one at a time. A
 * file whose fetch fails is not opened in a tab: past the first `await` the click no longer counts,
 * so the browser would block it. They are counted instead, and `failedMessage` says how many.
 */
export async function downloadEach(
    files: readonly DownloadableFile[],
    failedMessage: (failed: number) => string,
): Promise<void> {
    let failed = 0;
    for (const file of files) {
        try {
            await saveFile(file.url, file.fileName);
        } catch {
            failed += 1;
        }
    }
    if (failed > 0) notify.error(failedMessage(failed));
}
